import type { FC } from 'react';
import { memo, useEffect, useState } from 'react';
import { IMAGE_EXTENSIONS } from '../Records/components/FileUploadsItem/FileItem.tsx';
import { STORE_KEYS } from '../../../types/store.ts';
import { useStore } from '../../../contexts';
import { CircularProgress } from '@mui/material';
import { Image, Wrapper } from './CatalogueImage.styles';
import type { FileUrlResponse } from '../../../types';

interface CatalogueImageProps {
  file: FileUrlResponse;
}

const CatalogueImageBase: FC<CatalogueImageProps> = ({ file }) => {
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const { getItem } = useStore();
  const auth = getItem(STORE_KEYS.AUTH);

  useEffect(() => {
    (async () => {
      if (!auth?.jwtToken || !file?.url || imageSrc) return;
      setIsImageLoading(true);

      if (IMAGE_EXTENSIONS.includes(`${file?.fileExtension?.toLowerCase()}`)) {
        try {
          const response = await fetch(file?.url, {
            headers: {
              Authorization: auth.jwtToken,
            },
          });

          if (!response.ok) throw new Error('Failed to fetch image');

          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          setImageSrc(objectUrl);
        } catch {
          setImageSrc(null);
        } finally {
          setIsImageLoading(false);
        }
      }
    })();
  }, [imageSrc, file?.fileExtension, file?.url, auth?.jwtToken]);

  return (
    <>
      {isImageLoading ? (
        <Wrapper>
          <CircularProgress />
        </Wrapper>
      ) : (
        <Image src={imageSrc ?? ''} alt={file?.name ?? 'file_img'} />
      )}
    </>
  );
};

export const CatalogueImage = memo(CatalogueImageBase) as typeof CatalogueImageBase;
